import React, { useEffect, useMemo, useState } from "react";
import {
  Instagram,
  Facebook,
  Users,
  CalendarRange,
  Building2,
  ShieldCheck,
  ArrowUpRight,
  MapPin,
  Phone,
  Mail,
  Briefcase,
} from "lucide-react";
import LogoIntercam from "../assets/logo-1.png";
import KitEcommerce from "../assets/kit-e-commerce.png";
import HeroComision from "../assets/institucional/hero-frente-camara.jpeg";
import Publicacion1 from "../assets/institucional/publicacion-1.jpeg";
import Publicacion2 from "../assets/institucional/publicacion-2.jpeg";
import Publicacion3 from "../assets/institucional/publicacion-3.jpeg";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuth } from "../context/AuthProvider.tsx";
import { useNavigate } from "react-router-dom";
import { getOfertasLaborales, OfertaLaboral } from "../services/bolsaTrabajoService.ts";

const highlights = [
  {
    icon: Building2,
    title: "Red empresarial",
    description:
      "Acompañamos a comercios, industrias y emprendedores de Bolívar con capacitación, asesoramiento y representación.",
  },
  {
    icon: CalendarRange,
    title: "Agenda de eventos",
    description:
      "Generamos encuentros, ferias y rondas de negocios para impulsar conexiones reales entre socios.",
  },
  {
    icon: ShieldCheck,
    title: "Defensa gremial",
    description:
      "Representamos al sector ante organismos públicos y privados, cuidando los intereses de la comunidad empresarial.",
  },
];

const values = [
  {
    title: "Innovación y crecimiento",
    text: "Promovemos la digitalización y nuevas oportunidades para negocios locales.",
  },
  {
    title: "Trabajo colaborativo",
    text: "Construimos redes entre socios para potenciar alianzas y proyectos conjuntos.",
  },
  {
    title: "Compromiso local",
    text: "Impulsamos el desarrollo económico y social de Bolívar y su región.",
  },
];

const gallery = [
  {
    src: Publicacion1,
    alt: "Publicación institucional 1",
    title: "Actividad institucional",
    description: "Jornadas de trabajo junto a referentes del sector comercial y productivo local.",
  },
  {
    src: Publicacion2,
    alt: "Publicación institucional 2",
    title: "Participación comunitaria",
    description: "Encuentros y acciones para fortalecer el vínculo entre empresas, socios y comunidad.",
  },
  {
    src: Publicacion3,
    alt: "Publicación institucional 3",
    title: "Acciones de la Cámara",
    description: "Instancias de capacitación, representación y acompañamiento para nuestros socios.",
  },
];

const stats = [
  { value: "+70", label: "Años acompañando al sector" },
  { value: "+300", label: "Empresas y comercios vinculados" },
  { value: "+40", label: "Actividades anuales" },
];

const services = [
  "Asesoramiento institucional y gremial",
  "Capacitaciones para pymes, comercios y emprendedores",
  "Difusión comercial y networking empresarial",
  "Gestión de beneficios, convenios y alianzas",
  "Representación ante organismos públicos y privados",
  "Acompañamiento en procesos de digitalización",
];

const authoritiesVisible = [
  { role: "Presidente", name: "Jorge Aguirre" },
  { role: "Vicepresidente 1°", name: "Gilda Ikes" },
  { role: "Vicepresidente 2°", name: "Rubén Montero" },
  { role: "Secretario", name: "María Isabel Coviella" },
];

const authoritiesExtra = [
  { role: "Prosecretario", name: "María Alicia Rodríguez" },
  { role: "Tesorero", name: "Luis Germán Behrens" },
  { role: "Protesorero", name: "Pedro Pezzi" },
  { role: "Vocales Titulares", name: "Horacio Darretche · Gerardo López · Marcos Hernández · Donato Giarini · Yanina Navone" },
  { role: "Vocales Suplentes", name: "Federico Berón · Marcos Lede · Clara Cásaux Alsina · Nazareno Iberra · María María Álvarez" },
  { role: "Jurado", name: "Verónica Gallo · Ángel Berthole · Cirilo Curuchet · Norma Castellani" },
  { role: "Revisores de Cuentas", name: "Cristian Busquet · Ramiro Pagola" },
];

const Institutional: React.FC = () => {
  const { loginWithRedirect, isLoading: isAuth0Loading } = useAuth0();
  const { isAdmin, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const firstName = (user?.name || "Socio").split(" ")[0];
  const [ofertas, setOfertas] = useState<OfertaLaboral[]>([]);
  const [categoriaBolsa, setCategoriaBolsa] = useState<string>("Todas");
  const [showAutoridadesModal, setShowAutoridadesModal] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    getOfertasLaborales(true).then((items) => setOfertas(items || []));
  }, []);

  useEffect(() => {
    if (isAuth0Loading || !isAuthenticated) {
      setShowWelcome(false);
      return;
    }

    const raf = requestAnimationFrame(() => setShowWelcome(true));
    return () => cancelAnimationFrame(raf);
  }, [isAuth0Loading, isAuthenticated]);

  const categoriasBolsa = useMemo(() => {
    const all = ofertas.flatMap((o) => o.categorias || []);
    return ["Todas", ...Array.from(new Set(all))];
  }, [ofertas]);

  const ofertasFiltradas = useMemo(() => {
    if (categoriaBolsa === "Todas") return ofertas;
    return ofertas.filter((o) => (o.categorias || []).includes(categoriaBolsa));
  }, [ofertas, categoriaBolsa]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-900">
      <section
        className="relative w-full overflow-hidden py-12 text-white lg:py-16"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(30,64,175,0.65), rgba(79,70,229,0.56), rgba(37,99,235,0.56)), url(${HeroComision})`,
          backgroundSize: "cover",
          backgroundPosition: "top center",
        }}
      >
        <div className="mx-auto grid max-w-6xl gap-10 px-4 lg:grid-cols-2 lg:px-8">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold uppercase tracking-wide backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              Cámara comercial, industrial y de servicio
            </div>
            <h1 className="text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
              Somos Cámara Bolívar
            </h1>
            <p className="text-lg text-blue-100">
              Representamos, conectamos y fortalecemos a empresas, pymes y emprendedores de San Carlos de Bolívar y su zona de influencia.
              Junto a la Comisión Directiva impulsamos capacitaciones, eventos y oportunidades concretas de crecimiento.
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              {isAuth0Loading || isAuthenticated ? (
                <div className="rounded-xl bg-white/15 p-3 ring-1 ring-white/20 backdrop-blur">
                  {isAuth0Loading ? (
                    <div className="animate-pulse space-y-2">
                      <div className="mx-auto h-4 w-28 rounded bg-white/35" />
                      <div className="mx-auto h-10 w-56 rounded-xl bg-white/45" />
                    </div>
                  ) : (
                    <div
                      className={`transition-all duration-300 ease-out ${
                        showWelcome ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0"
                      }`}
                    >
                      <p className="text-sm font-semibold text-white">Hola {firstName} 👋</p>
                      <button
                        onClick={() => navigate(isAdmin ? "/admin" : "/inicio")}
                        className="mt-2 inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-blue-700 shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
                      >
                        Vamos al panel principal
                        <ArrowUpRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => loginWithRedirect()}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-blue-700 shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <Users className="h-4 w-4" />
                  Ingresar como socio
                </button>
              )}
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://www.instagram.com/camarabolivar/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/20 transition hover:bg-white/15"
                >
                  <Instagram className="h-4 w-4" />
                  Instagram
                  <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="https://www.facebook.com/camarabolivar/?locale=es_LA"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/20 transition hover:bg-white/15"
                >
                  <Facebook className="h-4 w-4" />
                  Facebook
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center">
            <div className="relative flex flex-col items-center gap-4 rounded-2xl bg-white/10 p-6 shadow-xl ring-1 ring-white/20 backdrop-blur">
              <img src={LogoIntercam} alt="Logo Red Intercam" className="h-32 w-32 object-contain" />
              <p className="text-sm font-semibold uppercase tracking-wide text-blue-50">
                Red Intercam de Cámaras
              </p>
              <p className="text-center text-blue-100">
                Conectamos cámaras, comercios y empresas en un ecosistema colaborativo para compartir oportunidades, servicios y crecimiento regional.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-10 lg:px-8 lg:py-12">
        <div className="overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-slate-100">
          <div className="space-y-12 px-6 py-10 lg:px-12">
            <section className="grid gap-6 lg:grid-cols-3">
              {highlights.map((item) => (
                <div key={item.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <item.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{item.description}</p>
                </div>
              ))}
            </section>

            <section className="grid gap-4 rounded-2xl border border-slate-100 bg-white p-6 sm:grid-cols-3">
              {stats.map((stat) => (
                <div key={stat.label} className="rounded-xl bg-slate-50 p-4 text-center ring-1 ring-slate-100">
                  <p className="text-2xl font-bold text-blue-700">{stat.value}</p>
                  <p className="mt-1 text-sm text-slate-600">{stat.label}</p>
                </div>
              ))}
            </section>

            <section className="grid items-center gap-8 rounded-2xl border border-slate-100 bg-slate-50/70 p-6 shadow-inner sm:grid-cols-2">
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                  Lo que nos mueve
                </p>
                <h2 className="text-2xl font-bold text-slate-900">Nuestra esencia</h2>
                <p className="text-sm text-slate-600">
                  Somos un espacio institucional para impulsar al sector comercial, industrial y de servicios del Partido de Bolívar. Promovemos redes de colaboración, profesionalización y herramientas de gestión para que cada socio mejore su competitividad.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {values.map((val) => (
                  <div key={val.title} className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-100">
                    <p className="text-sm font-semibold text-slate-900">{val.title}</p>
                    <p className="mt-1 text-xs text-slate-600">{val.text}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="grid gap-6 rounded-2xl border border-slate-100 bg-white p-6 lg:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Identidad institucional</p>
                <h2 className="mt-1 text-2xl font-bold text-slate-900">Misión y visión</h2>
                <p className="mt-3 text-sm text-slate-600">
                  <span className="font-semibold text-slate-800">Misión:</span> representar y fortalecer a las empresas y emprendedores de Bolívar,
                  promoviendo herramientas concretas para su crecimiento sostenible.
                </p>
                <p className="mt-2 text-sm text-slate-600">
                  <span className="font-semibold text-slate-800">Visión:</span> ser una institución referente en la región por su capacidad de articular al sector privado,
                  generar oportunidades y defender el desarrollo productivo local.
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Servicios institucionales</p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {services.map((service) => (
                    <li key={service}>• {service}</li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                  Nuestra comunidad en acción
                </p>
                <h2 className="text-2xl font-bold text-slate-900">Galería institucional</h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {gallery.map((img) => (
                  <article key={img.title} className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
                    <img src={img.src} alt={img.alt} className="h-48 w-full object-cover" loading="lazy" />
                    <div className="space-y-1 px-4 py-3">
                      <p className="text-sm font-semibold text-slate-800">{img.title}</p>
                      <p className="text-xs text-slate-600">{img.description}</p>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Oportunidades</p>
                  <h3 className="text-2xl font-bold text-slate-900">Bolsa de trabajo</h3>
                  <p className="text-sm text-slate-600">Búsquedas activas de empresas y comercios vinculados a la Cámara.</p>
                </div>
                <div className="rounded-full bg-violet-50 p-3 text-violet-700">
                  <Briefcase className="h-5 w-5" />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {categoriasBolsa.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setCategoriaBolsa(cat)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                      categoriaBolsa === cat ? 'bg-violet-600 text-white' : 'bg-violet-50 text-violet-700'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                {ofertasFiltradas.length === 0 ? (
                  <p className="text-sm text-slate-500">No hay búsquedas activas por el momento.</p>
                ) : (
                  ofertasFiltradas.slice(0, 6).map((o) => (
                    <article key={o.oferta_id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-base font-semibold text-slate-900">{o.titulo}</p>
                      <p className="text-sm text-slate-700">{o.empresa} · {o.ubicacion || 'Bolívar'}</p>
                      <p className="mt-1 text-xs text-slate-600">{o.modalidad || '—'} · {o.tipo || '—'}</p>
                      {!!(o.categorias || []).length && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {(o.categorias || []).map((c) => (
                            <span key={`${o.oferta_id}-${c}`} className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700">{c}</span>
                          ))}
                        </div>
                      )}
                      <p className="mt-2 text-sm text-slate-600 line-clamp-3">{o.descripcion}</p>
                      <p className="mt-2 text-xs font-semibold text-blue-700">Contacto: {o.contacto}</p>
                    </article>
                  ))
                )}
              </div>
              {isAdmin && (
                <div>
                  <a href="/bolsa-trabajo-admin" className="text-sm font-semibold text-blue-700 hover:text-blue-800">
                    Administrar bolsa de trabajo →
                  </a>
                </div>
              )}
            </section>

            <section className="grid gap-6 lg:grid-cols-3">
              <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900">Cómo participar</h3>
                <ul className="mt-3 space-y-2 text-sm text-slate-600">
                  <li>• Asociate para acceder a servicios institucionales y beneficios.</li>
                  <li>• Sumate a capacitaciones, jornadas y acciones sectoriales.</li>
                  <li>• Participá en redes comerciales y proyectos colaborativos.</li>
                </ul>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900">Comisión y autoridades</h3>
                <div className="mt-3 space-y-2 text-sm text-slate-600">
                  {authoritiesVisible.map((a) => (
                    <p key={a.role} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 ring-1 ring-slate-100">
                      <span className="font-semibold text-slate-800">{a.role}</span>
                      <span>{a.name}</span>
                    </p>
                  ))}
                </div>
                <button
                  className="mt-3 text-sm font-semibold text-blue-700 hover:text-blue-800"
                  onClick={() => setShowAutoridadesModal(true)}
                >
                  Ver más
                </button>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900">Contacto</h3>
                <div className="mt-3 space-y-2 text-sm text-slate-600">
                  <p className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-blue-600" />
                    Las Heras 45, Bolívar, Argentina
                  </p>
                  <p className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-blue-600" />
                    02314 42-7327
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-blue-600" />
                    camaracomercialbolivar@gmail.com
                  </p>
                  <p className="flex items-center gap-2">
                    <ArrowUpRight className="h-4 w-4 text-blue-600" />
                    <a href="https://camarabolivar.com.ar" target="_blank" rel="noreferrer" className="font-semibold text-blue-700 hover:text-blue-800">
                      camarabolivar.com.ar
                    </a>
                  </p>
                </div>
                <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
                  <iframe
                    title="Mapa Cámara Comercial de Bolívar"
                    src="https://www.google.com/maps?q=Las%20Heras%2045%2C%20Bol%C3%ADvar%2C%20Buenos%20Aires&output=embed"
                    className="h-56 w-full"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white shadow-lg">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-100">Nuevo</p>
                  <h3 className="mt-1 text-2xl font-bold">Catálogo de comercios y productos</h3>
                  <p className="mt-2 text-sm text-blue-100">
                    Explorá el catálogo oficial de socios. Podés ver tiendas, productos y contactar directo al comercio.
                  </p>
                </div>
                <a
                  href="/catalogo"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-blue-700 shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
                >
                  Ver catálogo
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </section>

            <section className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-6">
              <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr] lg:items-center">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-amber-700">Impulso e-commerce</p>
                  <h3 className="mt-1 text-2xl font-bold text-slate-900">Kit de accesorios para tu ecommerce</h3>
                  <p className="mt-2 text-sm text-slate-700">
                    Al asociarte a la Cámara, accedé a un kit con identidad institucional para mejorar las fotos de tus productos:
                    fondos, soportes, iluminación base y guía práctica para lograr imágenes más profesionales y vender mejor.
                  </p>
                  <p className="mt-2 text-xs font-semibold text-amber-800">
                    * Sujeto a aprobación por la entidad, stock limitado.
                  </p>
                  <button
                    onClick={() => loginWithRedirect()}
                    className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-5 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-amber-700"
                  >
                    <Users className="h-4 w-4" />
                    Quiero mi kit
                  </button>
                </div>
                <div className="overflow-hidden rounded-xl border border-amber-200 bg-white/70 p-2 shadow-sm">
                  <img src={KitEcommerce} alt="Kit e-commerce Cámara" className="h-full w-full rounded-lg object-cover" />
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-700">Sumate a la Cámara</p>
                  <h3 className="mt-1 text-2xl font-bold text-slate-900">Potenciá tu empresa con una red activa</h3>
                  <p className="mt-2 text-sm text-slate-600">Accedé a representación institucional, visibilidad y oportunidades concretas para crecer.</p>
                </div>
                <button
                  onClick={() => loginWithRedirect()}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-blue-700"
                >
                  <Users className="h-4 w-4" />
                  Quiero asociarme
                </button>
              </div>
            </section>

            <section className="grid gap-4 sm:grid-cols-2">
              <a
                href="https://www.instagram.com/camarabolivar/"
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-blue-500 hover:text-blue-600"
              >
                <span className="flex items-center gap-2">
                  <Instagram className="h-4 w-4" /> @camarabolivar
                </span>
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href="https://www.facebook.com/camarabolivar/?locale=es_LA"
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-blue-500 hover:text-blue-600"
              >
                <span className="flex items-center gap-2">
                  <Facebook className="h-4 w-4" /> /camarabolivar
                </span>
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </section>
          </div>
        </div>
      </div>

      {showAutoridadesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-xl bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Comisión completa</h3>
              <button className="rounded border px-3 py-1" onClick={() => setShowAutoridadesModal(false)}>Cerrar</button>
            </div>
            <div className="space-y-2 text-sm max-h-[70vh] overflow-auto">
              {authoritiesExtra.map((a) => (
                <div key={a.role} className="rounded-lg bg-slate-50 px-3 py-2 ring-1 ring-slate-100">
                  <p className="font-semibold text-slate-800">{a.role}</p>
                  <p className="text-slate-700">{a.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Institutional;
